import { R4 } from "@ahryman40k/ts-fhir-types";

import config from "@/config";
import { Assistant } from "@/global";

export async function fetchPrincipals(
  filters: { gln?: string; email?: string },
  authorization: string
): Promise<Assistant[]> {
  const practitionerRoles = await fetchPractitionerRoles(
    filters.gln
      ? { "practitioner.identifier": filters.gln }
      : { "practitioner.email": filters.email! },
    ["PractitionerRole:practitioner", "PractitionerRole:principal"],
    authorization
  );

  if (!practitionerRoles || practitionerRoles.length === 0) return [];

  const assistantPractitionerRole = practitionerRoles.find((practitionerRole) =>
    practitionerRole.id?.toLowerCase()?.startsWith(`${config.homeCommunity.prefix?.toLowerCase()}:`)
  );

  if (!assistantPractitionerRole) return [];

  const principalPractitionerRoleIds = assistantPractitionerRole.extension
    ?.filter(
      (ext) => ext.url === "https://bint.ch/fhir/StructureDefinition/PractitionerRole_principal"
    )
    .map((ext) => ext.valueReference?.reference?.replace("PractitionerRole/", ""));

  if (!principalPractitionerRoleIds || principalPractitionerRoleIds.length === 0) return [];

  const principalReferences = assistantPractitionerRole.contained
    ?.filter((entry) => principalPractitionerRoleIds.includes(entry.id))
    .map((entry) => entry as R4.IPractitionerRole)
    .map((entry) => entry.practitioner?.reference);

  return assistantPractitionerRole
    .contained!.filter((entry) => principalReferences!.includes(`#${entry.id}`))
    .map((entry) => entry as R4.IPractitioner)
    .map((practitioner) => {
      const gln = practitioner.identifier?.find((id) => id.system === "GLN")?.value || "";
      const firstName = practitioner.name?.[0]?.given?.[0] || "";
      const lastName = practitioner.name?.[0]?.family || "";
      return { gln, uapId: gln, firstName, lastName };
    });
}

export async function fetchPractitionerRoles(
  filters: Record<string, string>,
  includes: string[],
  authorization: string
): Promise<R4.IPractitionerRole[]> {
  const params = new URLSearchParams(filters);
  includes.forEach((include) => {
    params.append("_include", include);
  });
  const url = `${config.r4.practitionerRole}?${params.toString()}`;

  const response = await window.fetch(url, {
    method: "GET",
    headers: {
      Authorization: authorization,
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch practitioner roles");
  }

  const bundle = (await response.json()) as R4.IBundle;
  return bundle.entry?.map((entry) => entry.resource as R4.IPractitionerRole) || [];
}

export async function fetchParentOrganizations(
  url: string,
  authorization: string
): Promise<R4.IOrganization[]> {
  const response = await window.fetch(url, {
    method: "GET",
    headers: {
      Authorization: authorization,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch parent organization with URL: ${url}`);
  }

  const organization = (await response.json()) as R4.IOrganization;

  if (organization.partOf?.reference) {
    const parents = await fetchParentOrganizations(organization.partOf?.reference, authorization);
    return [...parents, organization];
  }

  return [organization];
}
